import React, { useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { AppSettings } from "../../config/app-settings.js";
import { Oval } from 'react-loader-spinner';
import MapComponent from "../../components/map/evacuation-map.jsx";
import { useEvacuationLayers } from "../../hooks/useEvacuation.js";
import useEvacuationStore from "../../stores/evacuation-store.js";
import useAuthStore from "../../stores/auth-store.js";

function EvacuationPortal() {
  const context = useContext(AppSettings);

  // Get authentication status
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = !!user;
  console.log(`🛂 Evacuation Portal - User is ${isAuthenticated ? 'authenticated' : 'not authenticated'}`);

  // Determine if we are on the evacuation portal page
  const location = useLocation();
  const pathName = location.pathname.split("/")[2];
  const isPortalPage = pathName === "evacuation-portal";
  console.log("🌐 EvacuationPortalSelector: isPortalPage =", isPortalPage);

  // Fetch layers only when authenticated AND on portal page
  const {
    isLoading: isLoadingZones,
    isError: hasZoneError,
    error: zoneError,
    isFetching
  } = useEvacuationLayers(isAuthenticated, { enabled: isPortalPage && isAuthenticated });

  // Store actions
  const isLoadingOrder = useEvacuationStore((state) => state.isLoadingOrder);

  // Setup app layout
  useEffect(() => {
    context.handleSetAppContentClass('p-0');
    context.handleSetAppSidebarTwo(true);
    context.handleSetAppSidebarWide(true);
    context.handleSetAppTopMenu(true);
    context.setAppSidebarEndToggled(false);

    return () => {
      console.log('🧹 Evacuation Portal unmounting - clearing store state');
      // Clear store state on page unmount
      useEvacuationStore.getState().clearAll();

      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppTopMenu(false);
      context.setAppSidebarEndToggled(false);
    };
  }, []);

  // Loading state
  if (isLoadingZones || isFetching) {
    return (
      <div className="loading-overlay">
        <div className="loading-content">
          <Oval
            height={80}
            width={80}
            color="var(--bs-app-theme)"
            wrapperStyle={{}}
            wrapperClass="justify-content-center"
            visible={true}
            ariaLabel='oval-loading'
            secondaryColor="var(--bs-app-theme)"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
          <h5 className="mt-3 text-white">Loading Map Layers</h5>
          <p className="text-gray-300">
            Large datasets may take a moment to render...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasZoneError) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="alert alert-danger m-4">
          <h4 className="alert-heading">
            <i className="fa fa-exclamation-triangle me-2"></i>
            Failed to Load Map Data
          </h4>
          <p className="mb-0">
            {zoneError?.message || "Could not load evacuation zone data."}
          </p>
          <hr />
          <p className="mb-0">
            <small>Please try refreshing the page or contact support.</small>
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Global loading overlay for order creation */}
      {isLoadingOrder && (
        <div className="loading-overlay">
          <div className="loading-content">
            <Oval
              height={80}
              width={80}
              color="var(--bs-app-theme)"
              wrapperStyle={{}}
              wrapperClass="justify-content-center"
              visible={true}
              ariaLabel='oval-loading'
              secondaryColor="var(--bs-app-theme)"
              strokeWidth={2}
              strokeWidthSecondary={2}
            />
            <h5 className="mt-3 text-white">Processing Evacuation Order...</h5>
            <p className="text-gray-300">
              Processing zone data and/or creating evacuation order...
            </p>
          </div>
        </div>
      )}

      {/* Map - no props needed, reads from store */}
      <div className="row gx-0">
        <div className="col-sm-12 p-0">
          <div className="card border-0 overflow-hidden bg-gray-800 text-white">
            <div className="card-body p-0">
              <MapComponent
                center={[49.8844, -119.4859]}
                zoom={9}
                height={`calc(100vh - var(--app-header-height, 60px) - var(--app-top-menu-height, 40px))`}
                width="100%"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EvacuationPortal;